// Chakra imports
import {
  Box,
  Flex,
  Icon,
  Select,
  SimpleGrid,
  useColorModeValue,
  Text,
  HStack,
  VStack,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Progress,
  Badge,
  Spinner,
  useToast,
  Avatar,
  Center,
} from "@chakra-ui/react";
// Custom components
import MiniStatistics from "components/card/MiniStatistics";
import IconBox from "components/icons/IconBox";
import Card from "components/card/Card.js";
import React, { useState, useEffect, useCallback } from "react";
import {
  MdRecordVoiceOver,
  MdAccessTime,
  MdAttachMoney,
  MdBarChart,
  MdTrendingUp,
  MdSettings,
  MdCheckCircle,
  MdError,
  MdPending,
  MdAssignment,
  MdSpeed,
  MdPerson,
  MdVideoCall,
  MdCalendarToday,
  MdSchedule,
  MdSmartToy,
} from "react-icons/md";
import DailyTraffic from "views/admin/default/components/DailyTraffic";
import TotalSpent from "views/admin/default/components/TotalSpent";
import TaskCompletionTrends from "views/admin/default/components/TaskCompletionTrends";

// Firebase imports
import { useAuth } from 'contexts/AuthContext';
import { getFirestore, collection, query, orderBy, limit, getDocs, where, Timestamp, onSnapshot } from 'firebase/firestore';

// Services
import { dashboardTaskService } from 'services/dashboardTaskService';

export default function UserReports() {
  // Chakra Color Mode
  const brandColor = useColorModeValue("brand.500", "white");
  const boxBg = useColorModeValue("secondaryGray.300", "whiteAlpha.100");
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const textColorSecondary = useColorModeValue("secondaryGray.600", "whiteAlpha.700");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");

  // State management
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    // Keep the two metrics we want to preserve
    totalCalls: 0,
    averageDuration: '0s',
    
    // New task-focused metrics
    totalTasksCompleted: 0,
    tasksCompletedThisWeek: 0,
    averageTaskCompletionTime: '0s',
    taskSuccessRate: 0,
    mostActiveAgent: { agentId: null, agentName: 'No agents', totalTasks: 0 },
    
    // Enhanced analytics
    tasksByCategory: [],
    taskCompletionTrends: [],
    toolUsageAnalytics: [],
    agentTaskPerformance: [],
    
    // Legacy data (will be phased out)
    totalCost: 0,
    averageCost: 0,
    successRate: 0,
    recentConversations: [],
    agentStats: [],
    dailySuccessData: [],
    callVolumeData: []
  });
  const [timeFilter, setTimeFilter] = useState('last-month');
  const [callVolumeTimeFilter, setCallVolumeTimeFilter] = useState('last-week');
  const [successRateTimeFilter, setSuccessRateTimeFilter] = useState('last-week');
  const [upcomingMeetings, setUpcomingMeetings] = useState([]);
  const [loadingMeetings, setLoadingMeetings] = useState(true);
  const [peopleCache, setPeopleCache] = useState({});

  // Firebase
  const { currentUser } = useAuth();
  const db = getFirestore();
  const toast = useToast();



  // Helper functions
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 17) return "Good afternoon";
    return "Good evening";
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const formatCost = (cost) => {
    if (typeof cost === 'number') {
      return `$${cost.toFixed(2)}`;
    }
    return '$0.00';
  };


  const getTimeFilterDate = (filterType = timeFilter) => {
    const now = new Date();
    switch (filterType) {
      case 'today':
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        return today;
      case 'last-week':
        const weekAgo = new Date(now);
        weekAgo.setDate(now.getDate() - 7);
        return weekAgo;
      case 'last-month':
        const monthAgo = new Date(now);
        monthAgo.setMonth(now.getMonth() - 1);
        return monthAgo;
      case 'last-3-months':
        const threeMonthsAgo = new Date(now);
        threeMonthsAgo.setMonth(now.getMonth() - 3);
        return threeMonthsAgo;
      default:
        const defaultMonthAgo = new Date(now);
        defaultMonthAgo.setMonth(now.getMonth() - 1);
        return defaultMonthAgo;
    }
  };

  // Generate chart data for daily trends - ONLY show periods with actual data
  const generateChartData = (conversations, startDate, filterType) => {
    const now = new Date();
    
    console.log(`📊 Generating chart data for ${conversations.length} conversations with filter: ${filterType}`);
    console.log(`📊 Filter date range: from ${startDate.toISOString()} to ${now.toISOString()}`);
    console.log(`📊 Sample conversation dates:`, conversations.slice(0, 3).map(c => ({
      id: c.conversationId,
      createdAt: c.createdAt.toISOString(),
      isInRange: c.createdAt >= startDate
    })));
    
    // Group conversations by time period (day or hour)
    const conversationsByPeriod = {};
    
    conversations.forEach(conv => {
      let periodKey;
      let periodLabel;
      
      if (filterType === 'today') {
        // Group by hour for today view
        const hour = conv.createdAt.getHours();
        periodKey = `hour-${hour}`;
        periodLabel = hour.toString().padStart(2, '0') + ':00';
      } else {
        // Group by day for other views
        const dateStr = conv.createdAt.toDateString(); // "Mon Jan 20 2025"
        periodKey = dateStr;
        periodLabel = conv.createdAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }
      
      console.log(`📊 Grouping conversation ${conv.conversationId} into period: ${periodKey} (${periodLabel})`);
      
      if (!conversationsByPeriod[periodKey]) {
        conversationsByPeriod[periodKey] = {
          label: periodLabel,
          conversations: [],
          date: conv.createdAt // Use actual conversation date
        };
      }
      
      conversationsByPeriod[periodKey].conversations.push(conv);
    });
    
    // Convert to array and sort by date
    const periods = Object.values(conversationsByPeriod).sort((a, b) => a.date - b.date);
    
    console.log(`📊 Found data for ${periods.length} time periods:`, periods.map(p => ({ label: p.label, calls: p.conversations.length })));
    
    // Calculate metrics for each period
    const chartData = periods.map(period => {
      const dayConversations = period.conversations;
      
      const successfulCalls = dayConversations.filter(conv => {
        const duration = conv.metadata?.call_duration_secs || 0;
        const hasGoodDuration = duration > 10;
        const hasTranscript = conv.transcript && conv.transcript.length > 1;
        const isExplicitlySuccessful = conv.analysis?.call_successful === 'success';
        const isStatusDone = conv.status === 'done';
        
        return isExplicitlySuccessful || (isStatusDone && (hasGoodDuration || hasTranscript));
      }).length;
      
      const successRate = dayConversations.length > 0 ? 
        (successfulCalls / dayConversations.length) * 100 : 0;
      
      console.log(`📊 ${period.label}: ${dayConversations.length} calls, ${successfulCalls} successful, ${successRate.toFixed(1)}% success rate`);
      
      return {
        label: period.label,
        successRate: Math.round(successRate),
        calls: dayConversations.length
      };
    });

    return {
      dailySuccessData: chartData,
      callVolumeData: chartData
    };
  };

  // Fetch dashboard data using new task-focused service
  const fetchDashboardData = useCallback(async () => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log('📊 Fetching dashboard data with new task-focused service...');

      // Convert time filter to service format
      const timeFilterMap = {
        'today': '24h',
        'last-week': '7d', 
        'last-month': '30d',
        'last-quarter': '90d'
      };
      const serviceTimeFilter = timeFilterMap[timeFilter] || '7d';
      
      // Fetch comprehensive dashboard data (always use 'all' agents since we removed the selector)
      const data = await dashboardTaskService.getDashboardData(
        currentUser.uid,
        serviceTimeFilter,
        'all'
      );

      console.log('📊 Received dashboard data:', data);
      setDashboardData(data);

    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast({
        title: 'Failed to load dashboard data',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [currentUser, timeFilter, callVolumeTimeFilter, successRateTimeFilter, db, toast]);

  const getTimeAgo = (date) => {
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  };

  // Fetch people directory for avatar colors
  useEffect(() => {
    const fetchPeople = async () => {
      if (!currentUser) return;
      
      try {
        const peopleQuery = query(collection(db, 'users', currentUser.uid, 'people'));
        const snapshot = await getDocs(peopleQuery);
        
        const peopleMap = {};
        snapshot.docs.forEach((doc) => {
          const personData = doc.data();
          peopleMap[doc.id] = {
            id: doc.id,
            name: personData.name || '',
            email: personData.email || '',
            color: personData.color || '#4ECDC4'
          };
          if (personData.email) {
            peopleMap[personData.email.toLowerCase()] = peopleMap[doc.id];
          }
          if (personData.name) {
            peopleMap[personData.name.toLowerCase()] = peopleMap[doc.id];
          }
        });
        setPeopleCache(peopleMap);
      } catch (error) {
        console.error('Failed to fetch people directory:', error);
      }
    };
    
    if (currentUser) {
      fetchPeople();
    }
  }, [currentUser, db]);

  // Fetch upcoming meetings from all agents
  useEffect(() => {
    if (!currentUser) {
      setLoadingMeetings(false);
      return;
    }

    setLoadingMeetings(true);
    const now = new Date();
    const threeMonthsFromNow = new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000);

    const fetchAgents = async () => {
      try {
        const agentsQuery = query(collection(db, 'agents'), where('admins', 'array-contains', currentUser.uid));
        const agentsSnapshot = await getDocs(agentsQuery);
        const agentIds = agentsSnapshot.docs.map(doc => doc.id);
        const agentsMap = {};
        agentsSnapshot.docs.forEach(doc => {
          agentsMap[doc.id] = { id: doc.id, ...doc.data() };
        });

        if (agentIds.length === 0) {
          setUpcomingMeetings([]);
          setLoadingMeetings(false);
          return;
        }

        const allMeetings = [];
        const unsubscribes = [];

        agentIds.forEach((agentId) => {
          const eventsQuery = query(
            collection(db, 'agents', agentId, 'events'),
            where('startTime', '>=', Timestamp.fromDate(now)),
            where('startTime', '<=', Timestamp.fromDate(threeMonthsFromNow))
          );

          const unsubscribe = onSnapshot(eventsQuery, (snapshot) => {
            const meetingsList = [];
            snapshot.forEach((doc) => {
              const data = doc.data();
              if (data.conferenceData && data.conferenceData.entryPoints && data.conferenceData.entryPoints.length > 0) {
                meetingsList.push({
                  id: doc.id,
                  agentId,
                  agent: agentsMap[agentId],
                  ...data,
                  startTime: data.startTime?.toDate(),
                  endTime: data.endTime?.toDate(),
                });
              }
            });

            // Update meetings list
            setUpcomingMeetings(prev => {
              const filtered = prev.filter(m => m.agentId !== agentId);
              const combined = [...filtered, ...meetingsList];
              combined.sort((a, b) => a.startTime - b.startTime);
              setLoadingMeetings(false);
              return combined.slice(0, 5); // Only top 5
            });
          });

          unsubscribes.push(unsubscribe);
        });

        return () => {
          unsubscribes.forEach(unsub => unsub());
        };
      } catch (error) {
        console.error('Failed to fetch meetings:', error);
        setLoadingMeetings(false);
      }
    };

    fetchAgents();
  }, [currentUser, db]);

  // Effects
  useEffect(() => {
    if (currentUser) {
      fetchDashboardData();
    }
  }, [currentUser, fetchDashboardData]);

  return (
    <Box>
      {/* Header with Greeting and Controls */}
      <Flex justify="space-between" align="center" mb="20px">
        <VStack align="start" spacing="1">
          <Text fontSize="sm" color={textColorSecondary}>
            My Workspace
          </Text>
          <Text fontSize="2xl" fontWeight="bold" color={textColor}>
            {getGreeting()}, {currentUser.displayName}
          </Text>
        </VStack>
        <HStack spacing="4">
          <Select 
            value={timeFilter}
            onChange={(e) => setTimeFilter(e.target.value)}
            size="sm" 
            maxW="150px"
          >
            <option value="last-month">Last month</option>
            <option value="last-week">Last week</option>
            <option value="today">Today</option>
          </Select>
          <Button size="sm" variant="ghost" onClick={fetchDashboardData} isLoading={loading}>
            <Icon as={MdSettings} />
          </Button>
        </HStack>
      </Flex>

      {/* Main Metrics Grid */}
      {loading ? (
        <Flex justify="center" align="center" h="200px">
          <Spinner size="xl" color={brandColor} />
          <Text ml="4" color={textColorSecondary}>Loading dashboard data...</Text>
        </Flex>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 5 }} gap="20px" mb="20px">
          {/* Keep the two metrics we want to preserve */}
          <MiniStatistics
            startContent={
              <IconBox
                w='56px'
                h='56px'
                bg={boxBg}
                icon={<Icon w='32px' h='32px' as={MdRecordVoiceOver} color={brandColor} />}
              />
            }
            name='Number of calls'
            value={dashboardData.totalCalls.toLocaleString()}
          />
          <MiniStatistics
            startContent={
              <IconBox
                w='56px'
                h='56px'
                bg={boxBg}
                icon={<Icon w='32px' h='32px' as={MdAccessTime} color={brandColor} />}
              />
            }
            name='Average duration'
            value={dashboardData.averageDuration}
          />
          
          {/* New task-focused metrics */}
          <MiniStatistics
            startContent={
              <IconBox
                w='56px'
                h='56px'
                bg='linear-gradient(90deg, #4481EB 0%, #04BEFE 100%)'
                icon={<Icon w='28px' h='28px' as={MdAssignment} color='white' />}
              />
            }
            name='Tasks completed'
            value={dashboardData.totalTasksCompleted.toLocaleString()}
          />
          <MiniStatistics
            startContent={
              <IconBox
                w='56px'
                h='56px'
                bg={dashboardData.taskSuccessRate >= 80 ? 'linear-gradient(90deg, #01B574 0%, #028F52 100%)' : boxBg}
                icon={<Icon w='28px' h='28px' as={MdCheckCircle} color={dashboardData.taskSuccessRate >= 80 ? 'white' : brandColor} />}
              />
            }
            name='Task success rate'
            value={`${dashboardData.taskSuccessRate.toFixed(1)}%`}
          />
          <MiniStatistics
            startContent={
              <IconBox
                w='56px'
                h='56px'
                bg={boxBg}
                icon={<Icon w='32px' h='32px' as={MdSpeed} color={brandColor} />}
              />
            }
            name='Avg task completion time'
            value={dashboardData.averageTaskCompletionTime}
          />
        </SimpleGrid>
      )}

      {/* Charts Section - Task Completion Trends */}

      <Box mb='20px'>
        <TaskCompletionTrends 
          taskCompletionTrends={dashboardData.taskCompletionTrends}
          timeFilter={callVolumeTimeFilter}
          onTimeFilterChange={setCallVolumeTimeFilter}
        />
      </Box>

      {/* Bottom Section - Upcoming Meetings and Recent Activity */}
      <SimpleGrid columns={{ base: 1, lg: 2 }} gap="20px" mb="20px">
        {/* Upcoming Meetings */}
        <Card p="6">
          <VStack align="stretch" spacing="4">
            <HStack justify="space-between">
              <Text fontSize="lg" fontWeight="bold" color={textColor}>
                Upcoming Meetings ({upcomingMeetings.length})
              </Text>
            </HStack>
            
            {loadingMeetings ? (
              <Center minH="100px">
                <Spinner size="md" color={brandColor} />
              </Center>
            ) : upcomingMeetings.length === 0 ? (
              <Box textAlign="center" py="8">
                <Icon as={MdVideoCall} w="40px" h="40px" color={textColorSecondary} mb="10px" />
                <Text color={textColorSecondary} fontSize="sm">
                  No upcoming meetings scheduled
                </Text>
              </Box>
            ) : (
              <Box overflowX="auto">
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th color={textColorSecondary} fontSize="xs">MEETING</Th>
                      <Th color={textColorSecondary} fontSize="xs">PARTICIPANTS</Th>
                      <Th color={textColorSecondary} fontSize="xs">DATE & TIME</Th>
                      <Th color={textColorSecondary} fontSize="xs">DURATION</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {upcomingMeetings.map((meeting) => {
                      const participants = [];
                      if (meeting.organizer) {
                        participants.push(meeting.organizer);
                      }
                      if (meeting.attendees) {
                        participants.push(...meeting.attendees.filter(
                          (attendee) => {
                            const attendeeEmail = (attendee.user?.email || attendee.email)?.toLowerCase();
                            const organizerEmail = meeting.organizer?.email?.toLowerCase();
                            return attendeeEmail !== organizerEmail;
                          }
                        ));
                      }

                      const formatMeetingTime = (startTime) => {
                        if (!startTime) return '';
                        const date = new Date(startTime);
                        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' +
                               date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                      };

                      const formatDuration = (startTime, endTime) => {
                        if (!startTime || !endTime) return '';
                        const durationMs = endTime - startTime;
                        const minutes = Math.floor(durationMs / 60000);
                        if (minutes < 60) return `${minutes}m`;
                        const hours = Math.floor(minutes / 60);
                        const remainingMinutes = minutes % 60;
                        return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
                      };

                      const getParticipantColor = (participant) => {
                        const email = (participant.user?.email || participant.email)?.toLowerCase();
                        const name = participant.user?.displayName || participant.displayName || participant.name;
                        const agent = meeting.agent;
                        
                        if (email === agent?.email?.toLowerCase()) {
                          return agent.color || '#4ECDC4';
                        }
                        if (email && peopleCache[email]) {
                          return peopleCache[email].color || '#4ECDC4';
                        }
                        if (name && peopleCache[name.toLowerCase()]) {
                          return peopleCache[name.toLowerCase()].color || '#4ECDC4';
                        }
                        return '#6B7280';
                      };

                      const isAgent = (participant) => {
                        const email = (participant.user?.email || participant.email)?.toLowerCase();
                        const agent = meeting.agent;
                        return email === agent?.email?.toLowerCase();
                      };

                      return (
                        <Tr key={meeting.id}>
                          <Td>
                            <HStack spacing="2">
                              <Icon as={MdVideoCall} w="16px" h="16px" color="brand.500" />
                              <Text fontSize="sm" fontWeight="600" color={textColor} noOfLines={1}>
                                {meeting.subject || 'Untitled Meeting'}
                              </Text>
                            </HStack>
                          </Td>
                          <Td>
                            <HStack spacing="-2" maxW="100px">
                              {participants.slice(0, 3).map((participant, idx) => {
                                const participantName = participant.user?.displayName || 
                                                      participant.displayName || 
                                                      participant.name || 
                                                      participant.email?.split('@')[0];
                                const participantColor = getParticipantColor(participant);
                                const isAgentParticipant = isAgent(participant);
                                
                                return (
                                  <Avatar
                                    key={idx}
                                    size="xs"
                                    name={participantName}
                                    icon={isAgentParticipant ? <Icon as={MdSmartToy} /> : <Icon as={MdPerson} />}
                                    bg={participantColor}
                                    border="2px"
                                    borderColor={boxBg}
                                  />
                                );
                              })}
                              {participants.length > 3 && (
                                <Text fontSize="xs" color={textColorSecondary} ml="1">
                                  +{participants.length - 3}
                                </Text>
                              )}
                            </HStack>
                          </Td>
                          <Td>
                            <HStack spacing="2">
                              <Icon as={MdCalendarToday} color={textColorSecondary} boxSize="3" />
                              <Text fontSize="xs" color={textColor}>
                                {formatMeetingTime(meeting.startTime)}
                              </Text>
                            </HStack>
                          </Td>
                          <Td>
                            <HStack spacing="2">
                              <Icon as={MdSchedule} color={textColorSecondary} boxSize="3" />
                              <Text fontSize="xs" color={textColor}>
                                {formatDuration(meeting.startTime, meeting.endTime)}
                              </Text>
                            </HStack>
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </Box>
            )}
          </VStack>
        </Card>

        {/* Recent Task Activity */}
        <Card p="6">
          <VStack align="stretch" spacing="4">
            <HStack justify="space-between">
              <Text fontSize="lg" fontWeight="bold" color={textColor}>
                Recent Task Activity
              </Text>
              <Button size="sm" variant="ghost" color="blue.500">
                View all tasks
              </Button>
            </HStack>
            
            {dashboardData.recentConversations.length > 0 ? (
              <VStack spacing="4" align="stretch">
                {dashboardData.recentConversations.map((task, index) => {
                  const getStatusProps = (status) => {
                    switch (status) {
                      case 'completed':
                        return { colorScheme: 'green', icon: MdCheckCircle, text: 'Completed' };
                      case 'failed':
                        return { colorScheme: 'red', icon: MdError, text: 'Failed' };
                      case 'in_progress':
                        return { colorScheme: 'blue', icon: MdPending, text: 'In Progress' };
                      default:
                        return { colorScheme: 'green', icon: MdCheckCircle, text: 'Completed' };
                    }
                  };
                  
                  const statusProps = getStatusProps(task.status);
                  
                  return (
                    <Box 
                      key={task.id}
                      p="3"
                      bg={boxBg}
                      borderRadius="md"
                      border="1px solid"
                      borderColor={borderColor}
                    >
                      <VStack align="stretch" spacing="2">
                        <HStack justify="space-between" align="start">
                          <VStack align="start" spacing="1" flex="1">
                            <Text fontSize="sm" fontWeight="semibold" color={textColor} noOfLines={1}>
                              {task.agentName}
                            </Text>
                            <Text fontSize="xs" color={textColorSecondary}>
                              {task.timeAgo}
                            </Text>
                          </VStack>
                          <Badge colorScheme={statusProps.colorScheme} size="sm" variant="subtle">
                            <HStack spacing="1">
                              <Icon as={statusProps.icon} boxSize="3" />
                              <Text>{statusProps.text}</Text>
                            </HStack>
                          </Badge>
                        </HStack>
                        
                        {/* Simplified task details - just task description */}
                        <Text fontSize="xs" color={textColorSecondary} noOfLines={2}>
                          {task.taskSummary || 'Task executed'}
                        </Text>
                      </VStack>
                    </Box>
                  );
                })}
              </VStack>
            ) : (
              <Box textAlign="center" py="8">
                <Text color={textColorSecondary} fontSize="md">
                  No recent task activity
                </Text>
                <Text fontSize="sm" color={textColorSecondary}>
                  Recent task executions will appear here
                </Text>
              </Box>
            )}
          </VStack>
        </Card>
      </SimpleGrid>
    </Box>
  );
}
